/**
 * Bone mapping utilities for mapping puppet bones to body parts
 *
 * This module provides utilities to map standard body parts (head, body, arms, legs)
 * to actual bones in a GLTF skeleton.
 *
 * Priority order for bone lookup:
 * 1. Exact bone name from puppet config (boneMapping)
 * 2. Fuzzy pattern matching for common naming conventions
 */

import type { Skeleton, Bone } from 'three';
import { usePuppetStore } from '../../../stores/puppetStore';

/**
 * Standard body part identifiers for puppet animation
 */
export type BodyPart =
  | 'head'
  | 'body'
  | 'leftArm'
  | 'rightArm'
  | 'leftLeg'
  | 'rightLeg';

/**
 * Common bone name patterns for each body part
 *
 * Patterns are ordered by priority (most specific first).
 * These patterns are matched case-insensitively and handle common variations
 * and prefixes (e.g., mixamorig:, CC_Base_, Bip01_).
 */
const BONE_NAME_PATTERNS: Record<BodyPart, string[]> = {
  head: ['head', 'neck'],
  body: ['hips', 'pelvis', 'hip', 'spine', 'chest', 'torso'],
  leftArm: [
    'leftarm',
    'left_arm',
    'l_arm',
    'lupperarm',
    'l_upperarm',
    'lefthand',
    'left_hand',
    'l_hand',
  ],
  rightArm: [
    'rightarm',
    'right_arm',
    'r_arm',
    'rupperarm',
    'r_upperarm',
    'righthand',
    'right_hand',
    'r_hand',
  ],
  leftLeg: [
    'leftleg',
    'left_leg',
    'l_leg',
    'lthigh',
    'l_thigh',
    'leftfoot',
    'left_foot',
    'l_foot',
  ],
  rightLeg: [
    'rightleg',
    'right_leg',
    'r_leg',
    'rthigh',
    'r_thigh',
    'rightfoot',
    'right_foot',
    'r_foot',
  ],
};

/**
 * Normalizes a bone name for matching by:
 * - Removing common prefixes (mixamorig:, CC_Base_, Bip01_, etc.)
 * - Converting to lowercase
 * - Removing separators (underscores, hyphens, spaces)
 *
 * @param name - The original bone name
 * @returns Normalized bone name for pattern matching
 */
const normalizeBoneName = (name: string): string => {
  return name
    .replace(/^(mixamorig:|CC_Base_|Bip\d+_)/i, '')
    .toLowerCase()
    .replace(/[_\s-]/g, '');
};

/**
 * Get a bone from a skeleton by body part
 *
 * Performs fuzzy matching on bone names to handle different naming conventions
 * used by various 3D modeling tools. Returns the first matching bone found,
 * or undefined if no match is found.
 *
 * @param skeleton - The Three.js Skeleton containing bones
 * @param bodyPart - The body part to find (head, body, leftArm, etc.)
 * @returns The matching Bone, or undefined if not found
 *
 * @example
 * ```typescript
 * const skeleton = puppetRef.current?.skeleton;
 * const headBone = getBoneByBodyPart(skeleton, 'head');
 * if (headBone) {
 *   headBone.rotation.x = Math.PI / 4; // Nod head
 * }
 * ```
 */
export function getBoneByBodyPart(
  skeleton: Skeleton | null,
  bodyPart: BodyPart
): Bone | undefined {
  if (!skeleton?.bones || skeleton.bones.length === 0) {
    return undefined;
  }

  // Priority 1: Try exact bone name from puppet config
  const configuredBoneName = usePuppetStore.getState().getBoneName(bodyPart);
  if (configuredBoneName) {
    const exactMatch = skeleton.bones.find(
      (bone) => bone.name === configuredBoneName
    );
    if (exactMatch) return exactMatch;
  }

  // Priority 2: Fall back to fuzzy pattern matching
  const patterns = BONE_NAME_PATTERNS[bodyPart];

  // Try each pattern for this body part
  for (const pattern of patterns) {
    const normalizedPattern = pattern.toLowerCase().replace(/[_\s-]/g, '');

    // Find first bone that matches this pattern
    const bone = skeleton.bones.find((bone) => {
      const normalizedName = normalizeBoneName(bone.name);
      // Use startsWith for more precise matching to avoid false positives
      // (e.g., 'rupperarm' shouldn't match 'lupperarm')
      return normalizedName.startsWith(normalizedPattern);
    });

    if (bone) return bone;
  }

  return undefined;
}

/**
 * Get all bones mapped to body parts for a skeleton
 *
 * Returns a map of body parts to bones. Missing bones will be undefined.
 * This is useful for getting a complete mapping of the skeleton structure
 * in one call.
 *
 * @param skeleton - The Three.js Skeleton to map
 * @returns Record mapping each body part to its bone (or undefined if not found)
 *
 * @example
 * ```typescript
 * const skeleton = puppetRef.current?.skeleton;
 * const bodyParts = mapAllBodyParts(skeleton);
 *
 * if (bodyParts.head) {
 *   console.log('Head bone found:', bodyParts.head.name);
 * }
 *
 * // Check which parts are missing
 * Object.entries(bodyParts).forEach(([part, bone]) => {
 *   if (!bone) console.warn(`Missing bone for ${part}`);
 * });
 * ```
 */
export function mapAllBodyParts(
  skeleton: Skeleton | null
): Record<BodyPart, Bone | undefined> {
  return {
    head: getBoneByBodyPart(skeleton, 'head'),
    body: getBoneByBodyPart(skeleton, 'body'),
    leftArm: getBoneByBodyPart(skeleton, 'leftArm'),
    rightArm: getBoneByBodyPart(skeleton, 'rightArm'),
    leftLeg: getBoneByBodyPart(skeleton, 'leftLeg'),
    rightLeg: getBoneByBodyPart(skeleton, 'rightLeg'),
  };
}
